import { useQuery } from "react-query";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { MediaAnalyticsApiParams } from "@circle-react/helpers/urlHelpers/internalApi/analytics/types";

interface AnalyticsParams {
  endDate?: string;
  signedId: string;
  startDate?: string;
}

type MetricType =
  | "views"
  | "unique_viewers"
  | "average_progress"
  | "average_duration"
  | "total_duration";

/**
 * Generic hook for fetching video analytics metrics
 */
export const useVideoAnalyticsMetric = (
  metricType: MetricType,
  { signedId, startDate, endDate }: AnalyticsParams,
) => {
  const params: MediaAnalyticsApiParams = {
    filters: {
      signed_id_filter: {
        key: "signed_id",
        value: signedId,
      },
    },
  };

  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;

  const fetchMetric = (): Promise<number> =>
    reactQueryGet(internalApi.analytics.media.overview[metricType](params));

  return useQuery(
    ["video-analytics", metricType, signedId, startDate, endDate],
    fetchMetric,
    {
      enabled: Boolean(signedId),
    },
  );
};

/**
 * Specific metric hooks - convenient wrappers around the generic hook
 */
export const useVideoAnalyticsViews = (params: AnalyticsParams) =>
  useVideoAnalyticsMetric("views", params);

export const useVideoAnalyticsUniqueViewers = (params: AnalyticsParams) =>
  useVideoAnalyticsMetric("unique_viewers", params);

export const useVideoAnalyticsAverageProgress = (params: AnalyticsParams) =>
  useVideoAnalyticsMetric("average_progress", params);

export const useVideoAnalyticsAverageDuration = (params: AnalyticsParams) =>
  useVideoAnalyticsMetric("average_duration", params);

export const useVideoAnalyticsTotalDuration = (params: AnalyticsParams) =>
  useVideoAnalyticsMetric("total_duration", params);
